import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { EVENT_TYPE_OPTIONS } from "../../shared/event-types";
import { SALES_STAGES, STAGE_LABELS, type SalesStage, hasPermission } from "../../shared/permissions";
import { LABEL_ADULTS, LABEL_CHILDREN_FREE, LABEL_CHILDREN_HALF } from "../../shared/child-price";
import { evaluationLabel } from "../../shared/tasting";
import { WORKBOOK_KEYS, cellText, eventFieldsFromEntry, matchVenueId, nameParts } from "../../shared/workbook";
import { ApiError, api } from "../api";
import { useSession } from "../App";
import { Banner, messageOf } from "./ui";

type VenueOption = { id: string; name: string };
type LoadedEvent = {
  event: {
    id: string;
    version: number;
    event_type: string | null;
    sales_stage: SalesStage;
    area: string | null;
    start_date: string | null;
    adults: number | null;
    children: number | null;
    venue_id: string | null;
  };
  contact: { full_name: string; given_name: string | null; family_name: string | null; phone: string | null; email: string | null } | null;
  workbook: Record<string, string>;
};

type Field = {
  key: string;
  label: string;
  kind: "text" | "date" | "email" | "number" | "textarea" | "type" | "stage" | "venue" | "priority" | "band" | "service";
  wide?: boolean;
  required?: boolean;
  suggestions?: boolean;
  legacy?: boolean;
};

const SECTIONS: { title: string; note: string; fields: Field[] }[] = [
  {
    title: "Γαλάζια ενότητα · Ραντεβού και αξιολόγηση",
    note: "Η επιθυμία του πελάτη. Το ενδιαφέρον είναι ένα από TP, HP, MP, LP, VLP ή OTHER.",
    fields: [
      { key: "serial", label: "Αύξων αριθμός", kind: "text" },
      { key: "given_name", label: "Ονομα", kind: "text", required: true },
      { key: "family_name", label: "Επωνυμο", kind: "text", required: true },
      { key: "visit_time", label: "Ωρα επίσκεψης", kind: "text" },
      { key: "visit_date", label: "Ημερομηνία επίσκεψης", kind: "date" },
      { key: "tasting_guests", label: "Ατομα στη δοκιμή", kind: "number" },
      { key: "table", label: "Αρ. τραπεζιού", kind: "text" },
      { key: "sales", label: "Υπεύθυνος πωλήσεων", kind: "text" },
      { key: "wanted_date", label: "Επιθυμητή ημερομηνία", kind: "date" },
      { key: "event_type", label: "Επιθυμητός τύπος", kind: "type" },
      { key: "adults", label: LABEL_ADULTS, kind: "number" },
      { key: "children", label: LABEL_CHILDREN_HALF, kind: "number" },
      { key: "children_free", label: LABEL_CHILDREN_FREE, kind: "number" },
      { key: "wanted_venue", label: "Επιθυμητός χώρος", kind: "text", suggestions: true },
      { key: "phone", label: "Τηλέφωνο", kind: "text" },
      { key: "email", label: "Email", kind: "email" },
      { key: "evaluation", label: "Αξιολόγηση", kind: "priority" },
      { key: "closed", label: "Εκλεισε", kind: "text" },
      { key: "follow_up", label: "Επόμενη επικοινωνία", kind: "date" },
      { key: "status_note", label: "Σημειώσεις και συνημμένα", kind: "textarea", wide: true },
      { key: "sales_stage", label: "Στάδιο", kind: "stage" },
      { key: "ok", label: "OK", kind: "text", legacy: true },
      { key: "mail_in", label: "Mail που έστειλαν", kind: "date", legacy: true },
      { key: "mail_out", label: "Πότε απαντήσαμε", kind: "date", legacy: true },
      { key: "area", label: "Περιοχή", kind: "text", legacy: true },
    ],
  },
  {
    title: "Κόκκινη ενότητα · Δέσμευση και συμφωνία",
    note: "Τα οριστικά στοιχεία. Μπορούν να διαφέρουν από την αρχική επιθυμία. Η ημερομηνία προκαταβολής είναι κρίσιμο πεδίο.",
    fields: [
      { key: "commitment", label: "Ημ. προφορικής δέσμευσης", kind: "date" },
      { key: "contract_date", label: "Ημ. συμφωνητικού", kind: "date" },
      { key: "deposit_date", label: "Ημ. προκαταβολής", kind: "date" },
      { key: "agreed_date", label: "Οριστική ημερομηνία", kind: "date" },
      { key: "agreed_type", label: "Οριστικός τύπος", kind: "text" },
      { key: "agreed_venue", label: "Οριστικός χώρος", kind: "text", suggestions: true },
      { key: "venue_id", label: "Συνδεδεμένος χώρος", kind: "venue" },
      { key: "file_code", label: "Κωδικός φακέλου", kind: "text" },
      { key: "agreed_guests", label: "Οριστικά άτομα", kind: "text" },
      { key: "installment_1", label: "1η δόση", kind: "text", legacy: true },
      { key: "installment_2", label: "2η δόση", kind: "text", legacy: true },
      { key: "installment_3", label: "3η δόση", kind: "text", legacy: true },
      { key: "paid_off", label: "Εξόφληση", kind: "text", legacy: true },
      { key: "final_meeting", label: "Τελικά ραντεβού", kind: "date", legacy: true },
    ],
  },
  {
    title: "Κίτρινη ενότητα · Λειτουργία εκδήλωσης",
    note: "Οσα χρειάζεται το service την ημέρα της εκδήλωσης. Εδώ χωρούν και σχόλια ή αναφορές σε αρχεία.",
    fields: [
      { key: "service_venue", label: "Χώρος πραγματοποίησης", kind: "text", suggestions: true },
      { key: "service_time", label: "Ζώνη ώρας", kind: "band" },
      { key: "menu", label: "Μενού", kind: "textarea", wide: true },
      { key: "setup", label: "Στήσιμο", kind: "textarea", wide: true },
      { key: "requirements", label: "Ειδικές απαιτήσεις", kind: "textarea", wide: true },
      { key: "dj", label: "DJ της επιχείρησης", kind: "text" },
      { key: "orchestra", label: "Ορχήστρα", kind: "text" },
      { key: "fireworks", label: "Πυροτεχνήματα", kind: "text" },
      { key: "animator", label: "Ανιματέρ", kind: "text" },
      { key: "games", label: "Παιχνίδια", kind: "text" },
      { key: "extras", label: "Άλλο", kind: "text" },
      { key: "church", label: "Εκκλησία της επιχείρησης", kind: "text" },
      { key: "allergies", label: "Αλλεργίες", kind: "textarea", wide: true },
      { key: "guest_list", label: "Λίστα καλεσμένων", kind: "textarea", wide: true },
      { key: "service_type", label: "Τύπος εκδήλωσης", kind: "service" },
      { key: "service_notes", label: "Σχόλια service", kind: "textarea", wide: true },
    ],
  },
];

const PRIORITY_OPTIONS = [
  ["TP", "TP · top"],
  ["HP", "HP · high"],
  ["MP", "MP · medium"],
  ["LP", "LP · low"],
  ["VLP", "VLP · very low"],
  ["OTHER", "OTHER · άλλο"],
] as const;
const BAND_OPTIONS = ["Μεσημέρι", "Απόγευμα-βράδυ"];
const SERVICE_OPTIONS = ["Γάμος", "Βάπτιση", "Γάμος και βάπτιση", "Εταιρική"];

const TYPE_OPTIONS = [
  ["", "Χωρίς τύπο"],
  ...EVENT_TYPE_OPTIONS,
  ["other", "Αλλο"],
] as const;

function blankValues(): Record<string, string> {
  const values: Record<string, string> = { sales_stage: "new_inquiry" };
  for (const section of SECTIONS) for (const field of section.fields) values[field.key] = values[field.key] ?? "";
  return values;
}

function valuesFrom(detail: LoadedEvent, venues: VenueOption[]): Record<string, string> {
  const values = blankValues();
  const parts = nameParts(detail.contact?.full_name ?? "", detail.contact?.given_name, detail.contact?.family_name);
  values.given_name = parts.given_name;
  values.family_name = parts.family_name;
  values.phone = detail.contact?.phone ?? "";
  values.email = detail.contact?.email ?? "";
  values.event_type = detail.event.event_type ?? "";
  values.sales_stage = detail.event.sales_stage;
  values.adults = detail.event.adults != null ? String(detail.event.adults) : "";
  values.children = detail.event.children != null ? String(detail.event.children) : "";
  values.area = detail.event.area ?? "";
  for (const key of WORKBOOK_KEYS) values[key] = cellText(key, detail.workbook[key] ?? "");
  const visit = values.visit_date || values.came || "";
  values.visit_date = visit;
  values.came = visit;
  values.start_date = detail.event.start_date ?? "";
  values.venue_id = detail.event.venue_id || matchVenueId(venues, values.agreed_venue) || matchVenueId(venues, values.wanted_venue) || "";
  return values;
}

export function EntryPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useSession();
  const canWrite = hasPermission(user.role, user.permissions, "events.write");
  const [venues, setVenues] = useState<VenueOption[]>([]);
  const [values, setValues] = useState<Record<string, string>>(blankValues);
  const [version, setVersion] = useState<number | null>(null);
  const [ready, setReady] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancel = false;
    setReady(false);
    setError(null);
    Promise.all([
      api<{ venues: VenueOption[] }>("/api/venues"),
      id ? api<LoadedEvent>(`/api/events/${id}`) : Promise.resolve(null),
    ])
      .then(([venueResult, detail]) => {
        if (cancel) return;
        const options = venueResult.venues.map((venue) => ({ id: venue.id, name: venue.name }));
        setVenues(options);
        if (detail) {
          setValues(valuesFrom(detail, options));
          setVersion(detail.event.version);
        } else {
          setValues(blankValues());
          setVersion(null);
        }
        setReady(true);
      })
      .catch((reason: unknown) => {
        if (cancel) return;
        setError(messageOf(reason));
        setReady(true);
      });
    return () => {
      cancel = true;
    };
  }, [id]);

  function setField(key: string, value: string) {
    setValues((current) => {
      const next = { ...current, [key]: value };
      if (key === "visit_date") next.came = value;
      if (key === "follow_up") next.follow_up_manual = "1";
      if (key === "venue_id" && value && !current.agreed_venue?.trim()) {
        const venue = venues.find((item) => item.id === value);
        if (venue) next.agreed_venue = venue.name;
      }
      return next;
    });
  }

  async function save() {
    if (!values.given_name?.trim() || !values.family_name?.trim()) {
      setError("Το ονομα και το επωνυμο είναι υποχρεωτικά.");
      return;
    }
    if (["adults", "tasting_guests", "children", "children_free"].some((key) => {
      const value = values[key]?.trim() ?? "";
      if (!value) return false;
      const min = key === "children" || key === "children_free" ? 0 : 1;
      return !/^\d+$/.test(value) || Number(value) < min || Number(value) > 100000;
    })) {
      setError("Τα άτομα γράφονται ως ακέραιος αριθμός.");
      return;
    }
    setSaving(true);
    setError(null);
    const payload = eventFieldsFromEntry(values);
    try {
      if (id) {
        await api(`/api/events/${id}`, { method: "PATCH", body: JSON.stringify({ ...payload, version }) });
        navigate(`/events/${id}`);
      } else {
        const created = await api<{ event_id: string }>("/api/events", { method: "POST", body: JSON.stringify(payload) });
        navigate(`/events/${created.event_id}`);
      }
    } catch (reason: unknown) {
      if (reason instanceof ApiError && reason.status === 409) setError("Η καταχώριση άλλαξε. Ανοιξε την ξανά.");
      else setError(messageOf(reason));
      setSaving(false);
    }
  }

  return (
    <>
      <header className="page-head">
        <div>
          <p className="meta"><Link to="/pipeline">Εκδηλώσεις</Link></p>
          <h1>{id ? "Επεξεργασία καταχώρισης" : "Νέα καταχώριση"}</h1>
          <p className="lede">Γράψε πρώτα την επιθυμία και μετά τα οριστικά στοιχεία. Τα κενά μένουν κενά. Η φάση βγαίνει από την πιο προχωρημένη ενότητα που έχει κάτι γραμμένο.</p>
        </div>
      </header>
      <Banner error={error} />
      {!ready ? <p>Φόρτωση καταχώρισης…</p> : null}
      {ready ? (
        <form
          className="entry-form"
          onSubmit={(event) => {
            event.preventDefault();
            void save();
          }}
        >
          <datalist id="venue-names">
            {venues.map((venue) => <option key={venue.id} value={venue.name} />)}
          </datalist>
          {SECTIONS.map((section) => (
            <article className="panel" key={section.title}>
              <h2>{section.title}</h2>
              <p className="meta">{section.note}</p>
              <div className="fields">
                {section.fields.filter((field) => !field.legacy || (values[field.key] ?? "").trim()).map((field) => (
                  <label key={field.key} className={field.wide ? "field-wide" : undefined}>
                    {field.label}
                    {field.kind === "textarea" ? (
                      <textarea value={values[field.key] ?? ""} disabled={!canWrite} onChange={(event) => setField(field.key, event.target.value)} />
                    ) : field.kind === "type" ? (
                      <select value={values.event_type ?? ""} disabled={!canWrite} onChange={(event) => setField("event_type", event.target.value)}>
                        {TYPE_OPTIONS.map(([value, label]) => <option key={value || "none"} value={value}>{label}</option>)}
                      </select>
                    ) : field.kind === "stage" ? (
                      <select value={values.sales_stage ?? "new_inquiry"} disabled={!canWrite} onChange={(event) => setField("sales_stage", event.target.value)}>
                        {SALES_STAGES.map((stage) => <option key={stage} value={stage}>{STAGE_LABELS[stage]}</option>)}
                      </select>
                    ) : field.kind === "priority" ? (
                      <select value={values.evaluation ?? ""} disabled={!canWrite} onChange={(event) => setField("evaluation", event.target.value)}>
                        <option value="">Χωρίς αξιολόγηση</option>
                        {priorityChoices(values.evaluation ?? "").map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                      </select>
                    ) : field.kind === "band" ? (
                      <select value={values.service_time ?? ""} disabled={!canWrite} onChange={(event) => setField("service_time", event.target.value)}>
                        <option value="">Χωρίς ζώνη</option>
                        {choiceOptions(BAND_OPTIONS, values.service_time ?? "").map((value) => <option key={value} value={value}>{value}</option>)}
                      </select>
                    ) : field.kind === "service" ? (
                      <select value={values.service_type ?? ""} disabled={!canWrite} onChange={(event) => setField("service_type", event.target.value)}>
                        <option value="">Χωρίς τύπο</option>
                        {choiceOptions(SERVICE_OPTIONS, values.service_type ?? "").map((value) => <option key={value} value={value}>{value}</option>)}
                      </select>
                    ) : field.kind === "venue" ? (
                      <select value={values.venue_id ?? ""} disabled={!canWrite} onChange={(event) => setField("venue_id", event.target.value)}>
                        <option value="">Χωρίς χώρο</option>
                        {venues.map((venue) => <option key={venue.id} value={venue.id}>{venue.name}</option>)}
                      </select>
                    ) : (
                      <input
                        type={inputType(field, values[field.key] ?? "")}
                        inputMode={field.kind === "number" ? "numeric" : undefined}
                        min={field.kind === "number" ? (field.key === "children" || field.key === "children_free" ? 0 : 1) : undefined}
                        step={field.kind === "number" ? 1 : undefined}
                        list={field.suggestions ? "venue-names" : undefined}
                        required={field.required}
                        value={values[field.key] ?? ""}
                        disabled={!canWrite}
                        onChange={(event) => setField(field.key, field.kind === "number" ? event.target.value.replace(/\D/g, "") : event.target.value)}
                      />
                    )}
                  </label>
                ))}
              </div>
            </article>
          ))}
          {canWrite ? (
            <button type="submit" disabled={saving}>{saving ? "Αποθήκευση…" : id ? "Αποθήκευση" : "Καταχώριση"}</button>
          ) : <p>Αυτή η σύνδεση δεν γράφει καταχωρίσεις.</p>}
        </form>
      ) : null}
    </>
  );
}

function priorityChoices(current: string): { value: string; label: string }[] {
  const options = PRIORITY_OPTIONS.map(([value, label]) => ({ value, label }));
  if (current && !options.some((option) => option.value === current)) return [{ value: current, label: evaluationLabel(current) }, ...options];
  return options;
}

function choiceOptions(options: string[], current: string): string[] {
  if (current && !options.includes(current)) return [current, ...options];
  return options;
}

function inputType(field: Field, value: string): string {
  if (field.kind === "date" && value && !/^\d{4}-\d{2}-\d{2}$/.test(value)) return "text";
  if (field.kind === "date") return "date";
  if (field.kind === "email") return "email";
  if (field.kind === "number") return "number";
  return "text";
}
